/**
 * What the mail rail draws from: the mailboxes, the reader's Favorites, and
 * each mailbox's server folders (for its Sent row).
 *
 * The folder lists are mostly not fetched here. Each open mailbox's "More
 * folders" block (`ImapFolders`) already reads its server's tree and reports it
 * up; doing it twice would spend a second IMAP login per mailbox on every
 * visit. Only a folded mailbox whose Sent is opened from Favorites is read
 * here (`ensureFolders`), because no tree is mounted to ask.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getImapFolders,
  getMailFavorites,
  getMailboxes,
  saveMailFavorites,
  saveMailboxOrder,
  type ImapFolder,
  type MailServerDefaults,
  type MailboxView,
} from "../../../../lib/mail";
import { moveMailbox, pinUsable, sentPath, togglePin } from "./view";

export interface MailRail {
  mailboxes: MailboxView[];
  defaults: MailServerDefaults | null;
  /** How many own mailboxes one person may keep. */
  max: number;
  favorites: string[];
  /** Pins whose mailbox still exists, in the order they were pinned. */
  shownFavorites: string[];
  isPinned: (ref: string) => boolean;
  togglePin: (ref: string) => void;
  /** Drop one mailbox before or after another: drawn at once, saved for the person. */
  moveMailbox: (id: string, target: string, after: boolean) => void;
  /** Each mailbox's Sent folder path, once its folder list has come in. */
  sentOf: (mailbox: string) => string | null;
  /** Whether the folder list for a mailbox has come in yet. */
  foldersKnown: (mailbox: string) => boolean;
  setFolders: (mailbox: string, folders: ImapFolder[]) => void;
  /** Read a mailbox's folder list when no tree has: a pinned Sent of a folded mailbox. */
  ensureFolders: (mailbox: string) => void;
  reload: () => Promise<void>;
}

export function useMailRail(onError: (msg: string) => void, saveFailed: string, orderFailed: string): MailRail {
  const [mailboxes, setMailboxes] = useState<MailboxView[]>([]);
  const [defaults, setDefaults] = useState<MailServerDefaults | null>(null);
  const [max, setMax] = useState(5);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [folders, setFolderMap] = useState<Record<string, ImapFolder[]>>({});

  const reload = useCallback(async () => {
    try {
      const res = await getMailboxes();
      setMailboxes(res.mailboxes);
      setDefaults(res.defaults);
      setMax(res.max);
    } catch {
      // The rail keeps what it had: a failed refresh of the counts is not a
      // reason to take the folder trees away.
    }
  }, []);

  useEffect(() => {
    void reload();
    getMailFavorites()
      .then((res) => setFavorites(res.favorites))
      .catch(() => setFavorites([]));
  }, [reload]);

  const ids = useMemo(() => new Set(mailboxes.map((m) => m.id)), [mailboxes]);
  const shownFavorites = useMemo(() => favorites.filter((ref) => pinUsable(ref, ids)), [favorites, ids]);

  const toggle = useCallback(
    (ref: string) => {
      const prev = favorites;
      // Saved without the pins of mailboxes that are gone, so the stored list
      // shrinks back to what can be drawn. Drawn at once, put back on failure.
      const next = togglePin(prev, ref).filter((r) => pinUsable(r, ids));
      setFavorites(next);
      saveMailFavorites(next).catch(() => {
        setFavorites(prev);
        onError(saveFailed);
      });
    },
    [favorites, ids, onError, saveFailed],
  );

  const move = useCallback(
    (id: string, target: string, after: boolean) => {
      const prev = mailboxes;
      const order = moveMailbox(
        prev.map((m) => m.id),
        id,
        target,
        after,
      );
      if (order.join("\t") === prev.map((m) => m.id).join("\t")) return;
      setMailboxes(order.map((boxId) => prev.find((m) => m.id === boxId)!));
      saveMailboxOrder(order).catch(() => {
        setMailboxes(prev);
        onError(orderFailed);
      });
    },
    [mailboxes, onError, orderFailed],
  );

  const setFolders = useCallback((mailbox: string, list: ImapFolder[]) => {
    setFolderMap((prev) => ({ ...prev, [mailbox]: list }));
  }, []);

  const ensureFolders = useCallback(
    (mailbox: string) => {
      if (mailbox in folders) return;
      getImapFolders(mailbox)
        .then((res) => setFolders(mailbox, res.ok ? res.folders : []))
        .catch(() => setFolders(mailbox, []));
    },
    [folders, setFolders],
  );

  return {
    mailboxes,
    defaults,
    max,
    favorites,
    shownFavorites,
    isPinned: (ref) => favorites.includes(ref),
    togglePin: toggle,
    moveMailbox: move,
    sentOf: (mailbox) => sentPath(folders[mailbox] ?? []),
    foldersKnown: (mailbox) => mailbox in folders,
    setFolders,
    ensureFolders,
    reload,
  };
}
